import { supabase } from '@/integrations/supabase/client';
import { PortfolioItem } from '@/hooks/profile';

export interface UpdatePortfolioItemData {
  id: string;
  title?: string;
  description?: string;
  image_url?: string;
  images?: Array<{
    id: string;
    url: string;
    order: number;
    caption: string;
  }>;
  status?: 'draft' | 'published';
  is_featured?: boolean;
  order?: number;
  slug?: string;
}

/**
 * PortfolioService - handles portfolio items (shots) stored in profiles.portfolio_items JSONB
 * Matches the CaseStudyService pattern for consistency
 */
export class PortfolioService {

  /**
   * Create a new portfolio item
   * Adds the item to the profiles.portfolio_items JSONB array
   */
  static async createPortfolioItem(data: Partial<PortfolioItem>): Promise<PortfolioItem> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw new Error('Authentication error: ' + authError.message);
    }
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get current user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, portfolio_items')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch profile: ' + profileError.message);
    }

    if (!profile) {
      throw new Error('Profile not found');
    }

    // Generate unique slug for the portfolio item
    const slug = await PortfolioService.generateSlug(
      data.slug || data.title || 'untitled-shot',
      user.id
    );

    // Create new portfolio item
    const newItem = {
      id: data.id || crypto.randomUUID(), // Use existing ID if provided (for autosave)
      title: data.title || 'Untitled Shot',
      description: data.description || '',
      image_url: data.image_url || '',
      images: data.images || [],
      status: data.status || 'draft',
      is_featured: data.is_featured || false,
      order: data.order || 0,
      slug: slug,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Add to portfolio items array
    const portfolioItems = profile.portfolio_items || [];

    // Check if item with this ID already exists (prevent duplicates from autosave)
    const existingItemIndex = portfolioItems.findIndex((item: any) => item.id === newItem.id);

    if (existingItemIndex !== -1) {
      // Item already exists - this should use updatePortfolioItem instead
      throw new Error(`Portfolio item with ID ${newItem.id} already exists. Use updatePortfolioItem to modify it.`);
    }

    const updatedPortfolioItems = [...portfolioItems, newItem];

    // Update the profile
    const { data: updatedProfile, error: updateError } = await supabase
      .from('profiles')
      .update({ portfolio_items: updatedPortfolioItems })
      .eq('id', user.id)
      .select('portfolio_items')
      .single();

    if (updateError) {
      throw new Error('Failed to create portfolio item: ' + updateError.message);
    }

    console.log('[PortfolioService] Created portfolio item with ID:', newItem.id);
    return PortfolioService.mapToPortfolioItem(newItem);
  }

  /**
   * Update a portfolio item
   * Updates the item within the profiles.portfolio_items JSONB array
   */
  static async updatePortfolioItem(id: string, updates: Partial<UpdatePortfolioItemData>): Promise<PortfolioItem> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw new Error('Authentication error: ' + authError.message);
    }
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get current user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, portfolio_items')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch profile: ' + profileError.message);
    }

    if (!profile) {
      throw new Error('Profile not found');
    }

    // Find and update the portfolio item
    const portfolioItems = profile.portfolio_items || [];
    const itemIndex = portfolioItems.findIndex((item: any) => item.id === id);
    
    if (itemIndex === -1) {
      throw new Error('Portfolio item not found');
    }

    // Update the item
    const updatedItem = {
      ...portfolioItems[itemIndex],
      ...updates,
      updated_at: new Date().toISOString()
    };

    // Replace the item in the array
    const updatedPortfolioItems = [...portfolioItems];
    updatedPortfolioItems[itemIndex] = updatedItem;

    // Update the profile
    const { data: updatedProfile, error: updateError } = await supabase
      .from('profiles')
      .update({ portfolio_items: updatedPortfolioItems })
      .eq('id', user.id)
      .select('portfolio_items')
      .single();

    if (updateError) {
      throw new Error('Failed to update portfolio item: ' + updateError.message);
    }

    return PortfolioService.mapToPortfolioItem(updatedItem);
  }

  /**
   * Delete a portfolio item
   * Removes the item from the profiles.portfolio_items JSONB array
   */
  static async deletePortfolioItem(id: string): Promise<void> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw new Error('Authentication error: ' + authError.message);
    }
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get current user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, portfolio_items')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch profile: ' + profileError.message);
    }

    if (!profile) {
      throw new Error('Profile not found');
    }

    // Remove the portfolio item
    const portfolioItems = profile.portfolio_items || [];
    const updatedPortfolioItems = portfolioItems.filter((item: any) => item.id !== id);

    // Update the profile
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ portfolio_items: updatedPortfolioItems })
      .eq('id', user.id);

    if (updateError) {
      throw new Error('Failed to delete portfolio item: ' + updateError.message);
    }
  }

  /**
   * Duplicate a portfolio item
   * Creates a copy of the item with a new ID and slug
   */
  static async duplicatePortfolioItem(id: string): Promise<PortfolioItem> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw new Error('Authentication error: ' + authError.message);
    }
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get the original item
    const originalItem = await PortfolioService.getPortfolioItem(id);
    if (!originalItem) {
      throw new Error('Portfolio item not found');
    }

    // Generate unique slug for the duplicate
    const slug = await PortfolioService.generateSlug(
      originalItem.slug || originalItem.title || 'untitled-shot',
      user.id
    );

    // Create the duplicate with a new ID and slug
    const duplicateData: Partial<PortfolioItem> = {
      id: crypto.randomUUID(),
      title: `${originalItem.title} (Copy)`,
      description: originalItem.description,
      image_url: originalItem.image_url,
      images: originalItem.images,
      status: 'draft', // Always create duplicates as drafts
      is_featured: false,
      order: 0,
      slug: slug,
    };

    // Create the duplicate item
    return await PortfolioService.createPortfolioItem(duplicateData);
  }

  /**
   * Get a single portfolio item by ID
   */
  static async getPortfolioItem(id: string): Promise<PortfolioItem | null> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw new Error('Authentication error: ' + authError.message);
    }
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get current user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('portfolio_items')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch profile: ' + profileError.message);
    }

    if (!profile) {
      return null;
    }

    // Find the portfolio item
    const portfolioItems = profile.portfolio_items || [];
    const item = portfolioItems.find((item: any) => item.id === id);
    
    if (!item) {
      return null;
    }

    return PortfolioService.mapToPortfolioItem(item);
  }

  /**
   * List all portfolio items for the current user
   */
  static async listPortfolioItems(): Promise<PortfolioItem[]> {
    // Check authentication
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw new Error('Authentication error: ' + authError.message);
    }
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get current user profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('portfolio_items')
      .eq('id', user.id)
      .single();

    if (profileError) {
      throw new Error('Failed to fetch profile: ' + profileError.message);
    }

    if (!profile) {
      return [];
    }

    const portfolioItems = profile.portfolio_items || [];
    return portfolioItems.map(PortfolioService.mapToPortfolioItem);
  }

  /**
   * Generate a unique slug for a portfolio item
   * Handles both new titles and existing slugs that might already have numeric suffixes
   */
  static async generateSlug(titleOrSlug: string, userId: string): Promise<string> {
    // Extract base slug - remove existing numeric suffix if present
    const baseSlug = titleOrSlug
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .replace(/-\d+$/, ''); // Remove trailing -1, -2, etc.

    // Get current user profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('portfolio_items')
      .eq('id', userId)
      .single();

    const portfolioItems = profile?.portfolio_items || [];
    let slug = baseSlug;
    let counter = 1;

    while (true) {
      // Check if slug exists in portfolio items
      const existing = portfolioItems.find((item: any) => item.slug === slug);

      if (!existing) break;

      slug = `${baseSlug}-${counter}`;
      counter++;
    }

    return slug;
  }

  /**
   * Map database item to PortfolioItem type
   */
  private static mapToPortfolioItem(data: Record<string, any>): PortfolioItem {
    return {
      id: data.id,
      title: data.title,
      description: data.description || '',
      image_url: data.image_url || '',
      images: data.images || [],
      created_at: data.created_at,
      updated_at: data.updated_at,
      is_featured: data.is_featured || false,
      status: data.status || 'draft',
      order: data.order || 0,
      slug: data.slug,
    };
  }
}
